import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { CalculusInfoRow } from '../CalculusInfoRow';
import { useAppTFunction } from '@retail/app/i18n';
import { RoundingCondition } from '@retail/calculus/types';
import { CalculusAreaAccordionDetails } from '../CalculusAreaAccordionDetails';
import { Image } from '@retail/components';
import { PriceChangeOutlined } from '@mui/icons-material';

interface Props {
  roundingCondition?: RoundingCondition;
  price?: number;
  openRoundingRuleDialog: () => void;
}

export const PriceRoundingConditionAccordion: React.FC<Props> = ({
  roundingCondition,
  price,
  openRoundingRuleDialog,
}) => {
  const t = useAppTFunction();
  return (
    <Accordion>
      <AccordionSummary>
        <CalculusInfoRow
          description={t('rounding.title')}
          price={roundingCondition && price}
          condition={
            roundingCondition && (
              <Box sx={{ display: 'inline', pl: 1 }}>
                <Typography component="span" color="text.secondary" variant="body2">
                  {roundingCondition.roundingRule.name}
                </Typography>
                {roundingCondition.roundingRule.centralOwner && (
                  <Tooltip
                    title={t('rounding.ruleOwner.central', {
                      owner: roundingCondition.roundingRule.centralOwner.name,
                    })}
                  >
                    <Image
                      src={roundingCondition.roundingRule.centralOwner.imgUrl}
                      alt={roundingCondition.roundingRule.centralOwner.name}
                      width={24}
                      height={24}
                      sx={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 2 }}
                    />
                  </Tooltip>
                )}
              </Box>
            )
          }
        />
      </AccordionSummary>
      <AccordionDetails>
        {roundingCondition ? (
          <Stack gap={2}>
            <CalculusAreaAccordionDetails
              bindingType={roundingCondition.bindingType}
              validFrom={roundingCondition.validFrom}
              validTo={roundingCondition.validTo}
              conditionValueDesc={t('calculus.rounding.ruleSetName')}
              conditionValue={
                <Button
                  variant="text"
                  size="small"
                  onClick={openRoundingRuleDialog}
                  endIcon={<PriceChangeOutlined />}
                >
                  {t('calculus.rounding.simulate')}
                </Button>
              }
            />
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ pl: 2 }}>
            {t('calculus.noRoundingCondition')}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
